// IP01 Up SIM: read the Owner's .xlsx IN THE BROWSER and project only the mapped SIM / price columns of the selected rows (E02).
// The workbook bytes and every other column (contact regions included) stay on the Owner's device: they are never sent, logged,
// previewed or stored. Only "<SIM display><TAB><price>" lines of the projection travel through the staged import.
export interface SheetInfo { name: string; path: string }
export interface Workbook { sheets: SheetInfo[]; read(sheet: string, cols: string[]): Promise<Map<number, Map<string, string>>> }

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream("deflate-raw");
  const stream = new Blob([data.slice().buffer as ArrayBuffer]).stream().pipeThrough(ds);
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** Minimal ZIP central-directory reader (stored / deflate entries). */
async function unzip(buf: ArrayBuffer): Promise<Map<string, () => Promise<string>>> {
  const u8 = new Uint8Array(buf), dv = new DataView(buf);
  let eocd = -1;
  for (let i = u8.length - 22; i >= Math.max(0, u8.length - 65_557); i--) if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  if (eocd < 0) throw new Error("Không đọc được tệp Excel (.xlsx).");
  const count = dv.getUint16(eocd + 10, true);
  let p = dv.getUint32(eocd + 16, true);
  const out = new Map<string, () => Promise<string>>();
  const dec = new TextDecoder("utf-8");
  for (let n = 0; n < count; n++) {
    if (dv.getUint32(p, true) !== 0x02014b50) throw new Error("Tệp Excel hỏng.");
    const method = dv.getUint16(p + 10, true), csize = dv.getUint32(p + 20, true), nlen = dv.getUint16(p + 28, true), xlen = dv.getUint16(p + 30, true), clen = dv.getUint16(p + 32, true);
    const local = dv.getUint32(p + 42, true);
    const name = dec.decode(u8.subarray(p + 46, p + 46 + nlen));
    const lnlen = dv.getUint16(local + 26, true), lxlen = dv.getUint16(local + 28, true);
    const start = local + 30 + lnlen + lxlen;
    const body = u8.subarray(start, start + csize);
    out.set(name, async () => dec.decode(method === 0 ? body : method === 8 ? await inflateRaw(body) : (() => { throw new Error("Kiểu nén Excel không hỗ trợ."); })()));
    p += 46 + nlen + xlen + clen;
  }
  return out;
}

const colOf = (ref: string) => (/^([A-Z]{1,3})[0-9]+$/.exec(ref) ?? [])[1] ?? "";
const rowOf = (ref: string) => Number((/^[A-Z]{1,3}([0-9]+)$/.exec(ref) ?? [])[1] ?? 0);

export async function openWorkbook(buf: ArrayBuffer): Promise<Workbook> {
  const z = await unzip(buf);
  const xml = (s: string) => new DOMParser().parseFromString(s, "application/xml");
  const wb = z.get("xl/workbook.xml");
  if (!wb) throw new Error("Không phải tệp Excel (.xlsx).");
  const wdoc = xml(await wb());
  const rels = z.get("xl/_rels/workbook.xml.rels");
  const relMap = new Map<string, string>();
  if (rels) for (const r of Array.from(xml(await rels()).getElementsByTagName("Relationship"))) relMap.set(r.getAttribute("Id") ?? "", r.getAttribute("Target") ?? "");
  const sheets: SheetInfo[] = Array.from(wdoc.getElementsByTagName("sheet")).map((s, i) => {
    const rid = s.getAttribute("r:id") ?? s.getAttributeNS("http://schemas.openxmlformats.org/officeDocument/2006/relationships", "id") ?? "";
    const target = relMap.get(rid) ?? `worksheets/sheet${i + 1}.xml`;
    return { name: s.getAttribute("name") ?? `Sheet${i + 1}`, path: target.startsWith("/") ? target.slice(1) : `xl/${target.replace(/^\.\//, "")}` };
  });
  let shared: string[] | null = null;
  const sharedStrings = async (): Promise<string[]> => {
    if (shared) return shared;
    const f = z.get("xl/sharedStrings.xml");
    shared = f ? Array.from(xml(await f()).getElementsByTagName("si")).map((si) => Array.from(si.getElementsByTagName("t")).map((t) => t.textContent ?? "").join("")) : [];
    return shared;
  };
  return {
    sheets,
    async read(sheetName: string, cols: string[]) {
      const s = sheets.find((x) => x.name === sheetName);
      const f = s ? z.get(s.path) : undefined;
      if (!f) throw new Error("Không tìm thấy sheet đã chọn.");
      const want = new Set(cols);
      const doc = xml(await f());
      const ss = await sharedStrings();
      const rows = new Map<number, Map<string, string>>();
      for (const c of Array.from(doc.getElementsByTagName("c"))) {
        const ref = c.getAttribute("r") ?? "", col = colOf(ref);
        if (!want.has(col)) continue;              // every other column is skipped before its value is even read
        const t = c.getAttribute("t"), v = c.getElementsByTagName("v")[0]?.textContent ?? "";
        const value = t === "s" ? ss[Number(v)] ?? "" : t === "inlineStr" ? Array.from(c.getElementsByTagName("t")).map((x) => x.textContent ?? "").join("") : v;
        const r = rowOf(ref);
        if (!rows.has(r)) rows.set(r, new Map());
        rows.get(r)!.set(col, value);
      }
      return rows;
    },
  };
}

/** Projects the selected rows to "<display>\t<price>" lines. Numbers are kept as written (an Excel number cell is its integer text). */
export function project(rows: Map<number, Map<string, string>>, simCol: string, priceCol: string, headerRows: number): { lines: string[]; rows: number } {
  const keys = [...rows.keys()].sort((a, b) => a - b).filter((r) => r > headerRows);
  const lines: string[] = [];
  for (const r of keys) {
    const m = rows.get(r)!;
    const sim = (m.get(simCol) ?? "").trim(), price = (m.get(priceCol) ?? "").trim();
    if (!sim && !price) continue;
    lines.push(`${sim.replace(/[\t\r\n;]/g, " ")}\t${price.replace(/[\t\r\n;]/g, " ")}`);
  }
  return { lines, rows: lines.length };
}

export async function sha256Hex(data: ArrayBuffer | string): Promise<string> {
  const bytes = typeof data === "string" ? new TextEncoder().encode(data) : data;
  const b = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(b)].map((x) => x.toString(16).padStart(2, "0")).join("");
}
